// Thư mục: src/stores/khoNhiemVuStore.js
import { defineStore } from "pinia";
// Import service đã cập nhật
import { DonHangApi } from "@/services/donHangService";

/**
 * Store này DÀNH RIÊNG cho Nhân Viên Kho (NV Kho)
 * để xem và xử lý các Điểm Dừng (Nhiệm vụ) được gán cho họ.
 */
export const useKhoNhiemVuStore = defineStore("khoNhiemVu", {
  state: () => ({
    isLoading: false,
    listError: null,
    tasks: [], // Danh sách các Điểm Dừng (DonHangDiemDungResponse)
    filterTrangThai: "CHO_SOAN_HANG", // Mặc định lọc task "Chờ Soạn"

    isSubmitting: false, // Dùng khi bấm nút "Báo cáo"
    submitError: null,
  }),

  getters: {
    // Đếm số lượng task đang chờ (để hiển thị trên tab)
    choSoanCount(state) {
      if (state.filterTrangThai === "CHO_SOAN_HANG") {
        return state.tasks.length;
      }
      // (Nếu muốn số này luôn_đúng_, cần 1 action riêng để đếm)
      return 0;
    },
  },

  actions: {
    /**
     * (ACTION CHÍNH) Tải danh sách nhiệm vụ (Điểm dừng) được gán cho TÔI
     */
    async fetchMyTasks() {
      this.isLoading = true;
      this.listError = null;
      try {
        const params = {
          trangThai: this.filterTrangThai,
        }; // Gọi API MỚI: getMyKhoTasks

        const data = await DonHangApi.getMyKhoTasks(params); // API này trả về List<DonHangDiemDungResponse>

        // (Trong BE, thầy đã sửa DTO này để chứa object 'donHang' cha)
        this.tasks = data || [];
      } catch (e) {
        console.error("Lỗi tải nhiệm vụ kho:", e);
        this.listError =
          e?.response?.data?.message || "Lỗi tải danh sách nhiệm vụ";
        this.tasks = [];
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * Đổi bộ lọc (tabs)
     */ async setFilterTrangThai(status) {
      this.filterTrangThai = status;
      await this.fetchMyTasks();
    },
    /**
     * (ACTION BÁO CÁO) NV Kho bấm nút "Xác nhận đã soạn xong"
     */
    async submitBaoCaoSoanXong(diemDungId, imageUrl) {
      this.isSubmitting = true;
      this.submitError = null;
      try {
        await DonHangApi.khoBaoCaoSoanXong(diemDungId, imageUrl); // <-- Gửi imageUrl
        await this.fetchMyTasks();
      } catch (e) {
        this.submitError = e?.response?.data?.message || "Lỗi khi gửi báo cáo";
        throw e;
      } finally {
        this.isSubmitting = false;
      }
    },

    async submitXacMinhGiaoHang(diemDungId, imageUrl) {
      this.isSubmitting = true;
      this.submitError = null;

      try {
        // Gọi API backend
        console.log(
          `[Kho] Gửi xác minh bàn giao: diemDungId=${diemDungId}, imageUrl=${imageUrl}`
        );
        await DonHangApi.khoXacMinhGiaoHang(diemDungId, imageUrl);

        // Sau khi gửi thành công -> tải lại danh sách
        console.log(
          "[Kho] ✅ Xác minh bàn giao thành công. Đang reload danh sách..."
        );
        await this.fetchMyTasks();
      } catch (e) {
        // --- GHI LOG CHI TIẾT ---
        console.error("[Kho] ❌ Lỗi xác minh giao hàng:", e);

        if (e.response) {
          // Lỗi từ phía backend (HTTP error)
          const status = e.response.status;
          const message =
            e.response.data?.message ||
            e.response.statusText ||
            "Lỗi không xác định từ server";

          console.error(
            `[Backend Error] Status: ${status} | Message: ${message}`
          );
          if (e.response.data?.trace) {
            console.debug("[Trace log backend]:", e.response.data.trace);
          }

          this.submitError = `[${status}] ${message}`;
          alert(`Lỗi xác minh: ${message}`);
        } else if (e.request) {
          // Request gửi đi nhưng không nhận được phản hồi
          console.error(
            "[Network Error] Request gửi đi nhưng không nhận phản hồi:",
            e.request
          );
          this.submitError =
            "Không nhận được phản hồi từ server. Vui lòng kiểm tra kết nối mạng hoặc server.";
          alert(this.submitError);
        } else {
          // Lỗi phát sinh trong quá trình xử lý JS (VD: JSON parse, undefined...)
          console.error("[Client Error] Lỗi cục bộ:", e.message);
          this.submitError = "Lỗi nội bộ: " + e.message;
          alert(this.submitError);
        }

        throw e; // Để component cha biết là lỗi vẫn xảy ra
      } finally {
        this.isSubmitting = false;
      }
    },

    async submitBaoCaoThieuHang(diemDungId, ghiChu) {
      this.isSubmitting = true;
      this.submitError = null;
      try {
        await DonHangApi.khoBaoCaoThieuHang(diemDungId, ghiChu);
        await this.fetchMyTasks(); // Tải lại list (task này sẽ biến mất)
      } catch (e) {
        console.error("Lỗi báo thiếu:", e);
        this.submitError =
          e?.response?.data?.message || "Lỗi khi gửi báo cáo thiếu";
        throw e;
      } finally {
        this.isSubmitting = false;
      }
    },

    async submitHoanThanhPickup(payload) {
      // payload = { diemDungId, tenNguoiLayHang, bienSoXeKhach, imageUrl }
      this.isSubmitting = true;
      this.submitError = null;
      try {
        await DonHangApi.khoHoanThanhPickup(
          payload.diemDungId,
          payload.tenNguoiLayHang,
          payload.bienSoXeKhach,
          payload.imageUrl
        );

        // Báo cáo thành công, tải lại danh sách
        await this.fetchMyTasks();
      } catch (e) {
        console.error("Lỗi hoàn thành pickup:", e);
        this.submitError = e?.response?.data?.message || "Lỗi khi gửi báo cáo";
        throw e; // Ném lỗi ra để component (Modal) biết
      } finally {
        this.isSubmitting = false;
      }
    },
  },
});
